// LeaderboardsIndexPage.js — /leaderboards
//
// Single popularity-sorted list of every public leaderboard on mchatai.com.
// Styled to match the rest of the mchatai.com SPA (light theme, .mp-page
// wrapper, light gray surfaces) per user feedback 2026-05-11.
//
// History:
//   - 2026-05-11 (current): re-skinned light to match Hub. Trophy SVG
//     replaces emoji 🏆.
//   - 2026-05-11 (initial simplification): "This whole 'Trending this week'
//     is pretty bogus. I don't have any users yet. So let's just go by what
//     is the most popular as a single list." Single popularity list; brand-
//     new boards (0 plays) slide to the bottom but stay visible.

import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db } from './firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';

// Canonical material-style trophy icon. Same path data as the in-game
// pill bridge (see functions/src/leaderboard.js:629). Single source of
// truth for the "trophy" iconography across the SPA.
function TrophyIcon({ size = 28, color = '#111827' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
         strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 21h8" />
      <path d="M12 17v4" />
      <path d="M7 4h10v5a5 5 0 0 1-10 0V4z" />
      <path d="M17 4h3v3a3 3 0 0 1-3 3" />
      <path d="M7 4H4v3a3 3 0 0 0 3 3" />
    </svg>
  );
}

export default function LeaderboardsIndexPage() {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Fetch the most recently active 200 — popularity sort happens client-
        // side because we want submitCount (live) as primary key. 200 is well
        // above any expected catalog size for v1.
        const snap = await getDocs(query(
          collection(db, 'leaderboards'),
          orderBy('lastSubmitAt', 'desc'),
          limit(200),
        ));
        const list = snap.docs.map((d) => ({
          boardId: d.id,
          ...d.data(),
          lastSubmitAt: d.data().lastSubmitAt?.toDate?.() ?? null,
        }));
        if (!cancelled) setBoards(list);
      } catch (err) {
        console.warn('[LeaderboardsIndex] load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sort + filter: popularity primary, recency tiebreak.
  const visible = useMemo(() => {
    let out = [...boards];
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((b) =>
        (b.projectTitle || b.boardId || '').toLowerCase().includes(q)
      );
    }
    out.sort((a, b) => {
      const sa = a.submitCount || 0;
      const sb = b.submitCount || 0;
      if (sb !== sa) return sb - sa;
      // tiebreak by recency
      const ta = a.lastSubmitAt?.getTime?.() || 0;
      const tb = b.lastSubmitAt?.getTime?.() || 0;
      return tb - ta;
    });
    return out;
  }, [boards, search]);

  return (
    <div className="mp-page">
      <div style={containerStyle}>
        <header style={headerStyle}>
          <div style={titleRowStyle}>
            <TrophyIcon size={36} />
            <h1 style={titleStyle}>Leaderboards</h1>
          </div>
          <p style={subtitleStyle}>
            Public high-score boards for skill-based mini-app games.
            Sign in to post your scores.
          </p>
        </header>

        {boards.length > 6 && (
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search games…"
            style={searchStyle}
            aria-label="Search leaderboards"
          />
        )}

        {loading && <div style={loadingStyle}>Loading…</div>}

        {!loading && visible.length > 0 && (
          <div style={gridStyle}>
            {visible.map((b) => (
              <BoardCard key={b.boardId} board={b} />
            ))}
          </div>
        )}

        {!loading && boards.length === 0 && (
          <div style={emptyStyle}>
            <p>No public leaderboards yet. Build a game in mChatAI+ and click "Add High Score" on the published product to start one.</p>
          </div>
        )}
        {!loading && boards.length > 0 && visible.length === 0 && search && (
          <div style={emptyStyle}>
            <p>No games match "{search}". Try a different search.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function BoardCard({ board }) {
  const title = board.projectTitle || board.boardId.replace(/-/g, ' ');
  const icon = board.iconURL || null;
  const firstChar = title.trim().charAt(0).toUpperCase() || '?';
  const playCount = board.submitCount || 0;
  const [hover, setHover] = React.useState(false);
  return (
    <Link
      to={`/leaderboards/${board.boardId}`}
      style={{ ...cardStyle, borderColor: hover ? '#3b82f6' : '#e5e7eb', boxShadow: hover ? '0 0 0 3px rgba(59,130,246,0.12)' : 'none' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={cardThumbWrapStyle}>
        {icon ? (
          <img src={icon} alt="" style={cardThumbStyle} />
        ) : (
          <div style={cardThumbPlaceholderStyle}>{firstChar}</div>
        )}
      </div>
      <div style={cardBodyStyle}>
        <div style={cardTitleStyle}>{title}</div>
        <div style={cardMetaStyle}>
          {playCount > 0 ? `${playCount.toLocaleString()} ${playCount === 1 ? 'play' : 'plays'}` : 'No plays yet'}
        </div>
      </div>
    </Link>
  );
}

// ── Styles ──
//
// Light theme matching Hub. .mp-page provides the off-white page bg + the
// shared site-wide font/padding. All cards/surfaces here use white + the
// gray-100/200 borders that the Hub uses (#e5e7eb / #f3f4f6).

const containerStyle = { maxWidth: 1000, margin: '0 auto', padding: '20px 24px 40px' };
const headerStyle = { textAlign: 'center', marginBottom: 28 };
const titleRowStyle = { display: 'inline-flex', alignItems: 'center', gap: 12 };
const titleStyle = { margin: 0, fontSize: 'clamp(1.8rem, 5vw, 2.4rem)', fontWeight: 800, color: '#111827' };
const subtitleStyle = { margin: '10px auto 0', maxWidth: 540, color: '#6b7280', lineHeight: 1.5, fontSize: 14 };
const loadingStyle = { padding: 40, textAlign: 'center', color: '#9ca3af' };
const emptyStyle = { padding: 40, textAlign: 'center', color: '#6b7280', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12 };

const searchStyle = {
  display: 'block',
  width: '100%',
  maxWidth: 360,
  margin: '0 auto 24px',
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  background: '#fff',
  color: '#111827',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 };

const cardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  textDecoration: 'none',
  color: '#111827',
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 14,
  padding: 12,
  transition: 'border-color 0.12s ease, box-shadow 0.12s ease',
};
const cardThumbWrapStyle = { flex: '0 0 auto' };
const cardThumbStyle = { width: 56, height: 56, borderRadius: 10, objectFit: 'cover', background: '#f3f4f6' };
const cardThumbPlaceholderStyle = {
  width: 56,
  height: 56,
  borderRadius: 10,
  background: 'linear-gradient(135deg, #e5e7eb, #d1d5db)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
  fontWeight: 800,
  color: '#6b7280',
  letterSpacing: '0.02em',
};
const cardBodyStyle = { flex: '1 1 auto', minWidth: 0 };
const cardTitleStyle = {
  fontWeight: 700,
  fontSize: 15,
  color: '#111827',
  textTransform: 'capitalize',
  marginBottom: 4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const cardMetaStyle = { fontSize: 12, color: '#6b7280' };
